import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/cn';

const buttonVariants = cva(
  // touch-manipulation removes 300ms tap delay; active:brightness-75 gives immediate visual feedback on tap
  'inline-flex items-center justify-center rounded-lg font-medium font-poppins transition-colors touch-manipulation select-none active:brightness-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:     'bg-brand-dark text-white hover:brightness-90 focus-visible:ring-brand-dark',
        accent:      'bg-brand-bright text-white hover:brightness-90 focus-visible:ring-brand-bright',
        destructive: 'bg-danger text-white hover:brightness-90 focus-visible:ring-danger',
        outline:     'border border-[#EDEDED] bg-ui-card text-ui-text hover:bg-[#EDEDED] dark:border-white/10 dark:hover:bg-white/10',
        ghost:       'text-ui-text hover:bg-[#EDEDED] dark:hover:bg-white/10',
        link:        'text-brand-dark underline-offset-4 hover:underline',
      },
      size: {
        // md and icon enlarge to 44px on mobile (min recommended touch target), shrink on md+
        sm:   'h-9 px-3 text-xs',
        md:   'h-11 px-4 text-sm md:h-10',
        lg:   'h-12 px-6 text-base',
        icon: 'h-11 w-11 md:h-9 md:w-9',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, children, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <svg className="mr-2 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
